import { createFileRoute } from "@tanstack/react-router";

import { CtaBand, PageHero, Prose } from "@/components/site/Page";
import { absoluteUrl, seoLinks, ogImage } from "@/lib/seo";

const TITLE = "La Junta de Andalucía y la educación | SIS Estepona";
const DESCRIPTION =
  "La Junta de Andalucía es la administración educativa de Andalucía. Así funcionan la autorización de centros, los requisitos mínimos y la Inspección educativa.";

export const Route = createFileRoute("/es/junta-de-andalucia-y-educacion")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: absoluteUrl("/es/junta-de-andalucia-y-educacion") },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:locale", content: "es_ES" },
      ...ogImage(),
    ],
    links: seoLinks("/es/junta-de-andalucia-y-educacion"),
  }),
  component: JuntaEducacion,
});

function JuntaEducacion() {
  return (
    <>
      <PageHero
        eyebrow="Administración educativa"
        title="La Junta de Andalucía y la educación"
        intro="La Junta de Andalucía es el gobierno autonómico de Andalucía y su consejería de educación es la administración que autoriza e inspecciona todos los centros docentes que funcionan en la región."
      />

      <Prose>
        <p>
          A través de la Consejería de Desarrollo Educativo y Formación Profesional, la Junta de
          Andalucía trabaja para que todo el alumnado de la comunidad tenga acceso a una educación
          de calidad e inclusiva, y para que cada centro cumpla lo que exige la normativa.
        </p>

        <h2>¿Esto vale para todos los colegios de España?</h2>
        <p>
          Sí. La educación se regula a nivel estatal pero la gestionan las 17 comunidades autónomas.
          Todos los centros –públicos, concertados y privados– necesitan autorización
          administrativa de su administración educativa para abrir y funcionar, y todos están
          sujetos a la Inspección Educativa. En Andalucía esa administración es la Junta de
          Andalucía. Los centros que imparten el sistema educativo de otro país, como el nuestro,
          se rigen además por la normativa específica de centros extranjeros en España (Real
          Decreto 806/1993).
        </p>

        <h2>Qué supone la supervisión en la práctica</h2>
        <p>
          <strong>Antes de abrir.</strong> La autorización solo se concede cuando el centro cumple
          los requisitos mínimos estatales del Real Decreto 132/2010: distribución y superficie por
          alumno, dimensiones de aulas y patio, higiene y seguridad, accesibilidad, número máximo de
          alumnos por grupo y titulación del personal. Cualquier cambio de instalaciones, enseñanzas
          o número de puestos escolares requiere una nueva resolución.
        </p>
        <p>
          <strong>Supervisión continua.</strong> Tras la autorización, el centro queda sujeto a la
          Inspección Educativa. Sus funciones están recogidas en la LOE (artículo 151): supervisar y
          controlar el funcionamiento pedagógico y organizativo del centro, supervisar la práctica
          docente y la función directiva, velar por el cumplimiento de las leyes y participar en la
          evaluación del sistema educativo. Los inspectores pueden acceder al centro y a su
          documentación.
        </p>
        <p>
          <strong>Cómo se planifica.</strong> En Andalucía la Inspección trabaja con el Plan General
          de Actuación 2023–2027, desarrollado en planes anuales por provincia. Cada centro tiene un
          inspector asignado como referencia permanente.
        </p>
        <p>
          <strong>Requisitos que nos afectan.</strong> Además de instalaciones y seguridad, la
          administración exige lengua y cultura españolas en la enseñanza. El español forma parte de cada día escolar, desde Infantil. Nuestra meta es que
          toda la enseñanza en sueco la impartan docentes con titulación docente sueca.
        </p>
        <p>
          <strong>Lo que no cubre la inspección.</strong> La Junta de Andalucía no evalúa el
          contenido del currículo sueco. Esa parte se apoya en la normativa sueca y en nuestra
          aprobación por la Agencia Nacional Sueca de Educación para la enseñanza complementaria de
          sueco; no somos un colegio sueco en el extranjero financiado por el Estado sueco.
        </p>
        <p>
          Fuentes:{" "}
          <a
            className="text-primary link-quiet"
            href="https://www.boe.es/buscar/act.php?id=BOE-A-2010-4132"
            target="_blank"
            rel="noopener noreferrer"
          >
            Real Decreto 132/2010 (BOE)
          </a>
          ,{" "}
          <a
            className="text-primary link-quiet"
            href="https://www.juntadeandalucia.es/boja/2023/146/2"
            target="_blank"
            rel="noopener noreferrer"
          >
            Plan General de Actuación 2023–2027 (BOJA)
          </a>{" "}
          y{" "}
          <a
            className="text-primary link-quiet"
            href="https://www.juntadeandalucia.es/educacion/portales/web/inspeccion-educativa/w/funciones-y-atribuciones"
            target="_blank"
            rel="noopener noreferrer"
          >
            funciones de la Inspección Educativa
          </a>
          .
        </p>

        <h2>Qué significa para las familias</h2>
        <p>
          La escolarización, el calendario escolar y los días festivos locales siguen las normas
          andaluzas. Nuestra enseñanza sigue el currículo sueco, pero el calendario tiene en cuenta
          tanto los festivos suecos como los andaluces, y el español está presente desde infantil.
        </p>
      </Prose>

      <CtaBand locale="es" />
      <div className="h-20" />
    </>
  );
}
